'use client'

import Image from 'next/image'
import Link from 'next/link'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { type MouseEvent, type MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import { resolveAltText } from '@/lib/image-utils'

export type CarouselImage = {
  url: string
  alt?: string
}

export default function ImageCarousel({
  images,
  linkHref,
  onLinkClick,
  className = '',
  imageClassName = '',
  controlsClassName = '',
  showCounter = true,
  showDots = true,
  priority = false,
  sizes,
}: {
  images: CarouselImage[]
  linkHref?: string
  onLinkClick?: MouseEventHandler<HTMLAnchorElement>
  className?: string
  imageClassName?: string
  controlsClassName?: string
  showCounter?: boolean
  showDots?: boolean
  priority?: boolean
  sizes?: string
}) {
  const cleaned = useMemo(() => {
    const out: CarouselImage[] = []
    const seen = new Set<string>()
    for (const img of Array.isArray(images) ? images : []) {
      const url = typeof img?.url === 'string' ? img.url.trim().replace(/^['"]|['"]$/g, '') : ''
      if (!url) continue
      const key = url.toLowerCase()
      if (seen.has(key)) continue
      seen.add(key)
      const alt = typeof img?.alt === 'string' ? img.alt : ''
      out.push({ url, alt })
    }
    return out
  }, [images])

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false })
  const [selectedIndex, setSelectedIndex] = useState(0)

  const scrollPrev = useCallback((e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback((e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const scrollTo = useCallback((index: number, e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (emblaApi) emblaApi.scrollTo(index)
  }, [emblaApi])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, onSelect])

  const count = cleaned.length
  const canNavigate = count > 1

  if (count === 0) return null

  return (
    <div className={`group relative ${className}`} style={{ touchAction: 'pan-y' }}>
      
      {/* Embla Viewport */}
      <div className="overflow-hidden w-full h-full rounded-none" ref={emblaRef}>
        <div className="flex w-full h-full touch-pan-y">
          {cleaned.map((img, i) => {
            const imageNode = (
              <Image
                src={img.url}
                alt={resolveAltText(img.alt, img.url, false)}
                className={`object-cover ${imageClassName}`}
                fill
                sizes={sizes || "(max-width: 768px) 100vw, 50vw"}
                priority={(priority && i === 0) || false}
                loading={(priority && i === 0) ? 'eager' : 'lazy'}
                draggable="false" // Prevent native html drag causing conflicts
              />
            )

            return (
              <div key={i} className="flex-[0_0_100%] min-w-0 h-full relative cursor-grab active:cursor-grabbing">
                {linkHref ? (
                  <Link href={linkHref} className="block w-full h-full relative" onClick={onLinkClick}>
                    {imageNode}
                  </Link>
                ) : (
                  imageNode
                )}
              </div>
            )
          })}
        </div>
      </div>

      {canNavigate && (
        <>
          <button
            type="button"
            onClick={scrollPrev}
            aria-label="Previous image"
            disabled={selectedIndex === 0}
            className={`absolute left-3 top-1/2 -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md border border-white/20 transition-all duration-300 opacity-0 group-hover:opacity-100 hover:bg-black/70 hover:scale-105 active:scale-95 disabled:opacity-0 ${controlsClassName}`}
          >
            <ChevronLeft size={22} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            onClick={scrollNext}
            aria-label="Next image"
            disabled={selectedIndex === count - 1}
            className={`absolute right-3 top-1/2 -translate-y-1/2 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-black/40 text-white backdrop-blur-md border border-white/20 transition-all duration-300 opacity-0 group-hover:opacity-100 hover:bg-black/70 hover:scale-105 active:scale-95 disabled:opacity-0 ${controlsClassName}`}
          >
            <ChevronRight size={22} strokeWidth={2.5} />
          </button>
        </>
      )}

      {/* Modern Dots Indicator for mobile */}
      {showDots && count > 1 && (
        <div className="absolute bottom-3 left-0 right-0 z-10 flex justify-center gap-1.5 px-4 pointer-events-none">
            {cleaned.map((_, i) => (
                <button
                    key={i}
                    onClick={(e) => scrollTo(i, e)}
                    aria-label={`Go to slide ${i + 1}`}
                    className={`h-1.5 rounded-full pointer-events-auto transition-all duration-300 shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${i === selectedIndex ? 'w-5 bg-white' : 'w-1.5 bg-white/50 hover:bg-white/80'}`}
                />
            ))}
        </div>
      )}

      {/* Legacy Counter Badge */}
      {showCounter && count > 1 && (
        <div className="absolute top-3 right-3 z-10 rounded-full bg-black/60 shadow-md text-white text-[11px] tracking-wider font-semibold px-2.5 py-1 backdrop-blur-md border border-white/20 pointer-events-none">
          {selectedIndex + 1} / {count}
        </div>
      )}
    </div>
  )
}
